import { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { useTheme } from 'styled-components';

import { shallowEqual } from 'react-redux';
import { Widget } from 'jamespot-front-business';
import { EditorInputNumber } from './components/EditorInputNumber';
import { EditorInputSelect } from './components/EditorInputSelect';
import { EditorInputColor } from './components/EditorInputColor';

import { useAppSelector, useAppDispatch } from '../../redux/store';

export const EditorTabWidgetSelection = ({ uniqid }: { uniqid: string }) => {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const intl = useIntl();

    const styles = useAppSelector((state) => Widget.selectors.selectWidgetWrapperStyle(state, uniqid), shallowEqual);

    const initialValues = useMemo(() => {
        return {
            color: styles && styles['color'] ? String(styles['color']) : theme.color.black,
            backgroundColor:
                styles && styles['background-color'] ? String(styles['background-color']) : theme.color.white,
            padding: styles && styles['padding'] ? String(styles['padding']) : '0',
            borderRadius: styles && styles['border-radius'] ? String(styles['border-radius']) : '0',
            borderColor: styles['border-color'] ? String(styles['border-color']) : theme.color.black,
            borderWidth: styles['border-width'] ? String(styles['border-width']) : '0',
            borderStyle: styles['border-style'] ? String(styles['border-style']) : 'none',
        };
    }, [styles, theme]);

    const handleChange = useCallback(
        (name: string, value: string) => {
            dispatch(
                Widget.slice.actions.setWidgetWrapperStyle({
                    uniqid,
                    styles: { [name]: value },
                }),
            );
        },
        [uniqid, dispatch],
    );

    return (
        <>
            <EditorInputColor
                label={'STYLE_Background_Color'}
                initialValue={initialValues.backgroundColor}
                handleChange={(backgroundColorValue) => handleChange('background-color', backgroundColorValue)}
            />
            <EditorInputColor
                label={'STYLE_Text_Color'}
                initialValue={initialValues.color}
                handleChange={(color) => handleChange('color', color)}
            />
            <hr />
            <EditorInputNumber
                label={'STYLE_Padding'}
                initialValue={initialValues.padding}
                handleChange={(paddingValue) => handleChange('padding', String(paddingValue))}
            />
            <EditorInputNumber
                label={'STYLE_Radius'}
                initialValue={initialValues.borderRadius}
                handleChange={(borderRadiusValue) => handleChange('border-radius', String(borderRadiusValue))}
            />
            <EditorInputSelect
                label={'STYLE_Border_Style'}
                initialValue={initialValues.borderStyle}
                options={[
                    { label: intl.formatMessage({ id: 'STYLE_Border_Style_None' }), value: 'none' },
                    { label: intl.formatMessage({ id: 'STYLE_Border_Style_Solid' }), value: 'solid' },
                    { label: intl.formatMessage({ id: 'STYLE_Border_Style_Dashed' }), value: 'dashed' },
                    { label: intl.formatMessage({ id: 'STYLE_Border_Style_Dotted' }), value: 'dotted' },
                ]}
                required={false}
                handleChange={(borderStyleValue) => {
                    handleChange('border-style', borderStyleValue);
                    borderStyleValue !== 'none' && handleChange('border-color', theme.color.black);
                    borderStyleValue !== 'none' && handleChange('border-width', '1');
                }}
            />
            {typeof initialValues.borderStyle === 'string' && initialValues.borderStyle !== 'none' && (
                <EditorInputColor
                    label={'STYLE_Border_Color'}
                    initialValue={initialValues.borderColor}
                    handleChange={(borderColorValue) => handleChange('border-color', borderColorValue)}
                />
            )}
            {typeof initialValues.borderStyle === 'string' && initialValues.borderStyle !== 'none' && (
                <EditorInputNumber
                    label={'STYLE_Border_Width'}
                    initialValue={initialValues.borderWidth}
                    handleChange={(borderWidthValue) => handleChange('border-width', borderWidthValue)}
                />
            )}
        </>
    );
};
